/**
 * Standardized shift period definitions used across all ingestion and display paths.
 *
 * Ranges:
 *   breakfast  — 05:00 – 10:59
 *   lunch      — 11:00 – 15:59
 *   dinner     — 16:00 – 21:59
 *   late_night — 22:00 – 04:59
 */

export type ShiftPeriod = 'breakfast' | 'lunch' | 'dinner' | 'late_night';

export const SHIFT_LABELS: Record<ShiftPeriod, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  late_night: 'Late night',
};

/**
 * Determine the shift period from a time string (HH:MM:SS, HH:MM, or HH).
 * Returns `null` when the input is missing or unparseable.
 */
export function getShiftPeriod(timeStr: string | null | undefined): ShiftPeriod | null {
  if (!timeStr || typeof timeStr !== 'string') return null;

  const trimmed = timeStr.trim();
  const match = trimmed.match(/^(\d{1,2})/);
  if (!match) return null;

  const hour = parseInt(match[1], 10);
  if (Number.isNaN(hour) || hour < 0 || hour > 23) return null;

  if (hour >= 5 && hour < 11) return 'breakfast';
  if (hour >= 11 && hour < 16) return 'lunch';
  if (hour >= 16 && hour < 22) return 'dinner';
  return 'late_night';
}

/**
 * Same as `getShiftPeriod` but accepts a Date object.
 */
export function getShiftPeriodFromDate(date: Date): ShiftPeriod {
  const hour = date.getHours();
  if (hour >= 5 && hour < 11) return 'breakfast';
  if (hour >= 11 && hour < 16) return 'lunch';
  if (hour >= 16 && hour < 22) return 'dinner';
  return 'late_night';
}
